/**
 * Admin — Revenue Calculation, term / dosage / titration and cadence timeline (card 1244).
 *
 * Rendered inside an expanded product row. It answers, for ONE product × dosage × term,
 * every question in ADMIN-PAGE-PLAN.md §Cadence model shown explicitly: how many fills the
 * term ships, what each fill contains, which vial the step dispenses, where cost changes,
 * and which figures are per shipment rather than per month.
 *
 * 🔑 THE SELECTED DOSAGE IS USED, NEVER THE LOWEST. The ladder is a control, not a legend:
 * `selectedDosageId` drives every figure below it. It opens on the catalog DEFAULT step
 * (`product_dosages.is_default` — the same step checkout pre-selects), not on `dosages[0]`,
 * and the two are NOT the same product-by-product. See
 * docs/oval/project-oval-revenue/findings/DOSAGE-SOURCE-OF-TRUTH.md: dosage is a
 * point-of-sale fact, so a timeline that silently showed the cheapest step would
 * under-report cost on every titrated customer.
 *
 * 🔑 NO ARITHMETIC IS INVENTED HERE. `shipments` and `shipmentMonths` are read off
 * `row.cadence`, which the server built from `supplyTermService.getShipments`. The
 * component recomputes neither. (A grep for the ceil call is the check — but note this
 * comment would match a bare `ceil` pattern, so grep for `Math.` followed by `ceil`.)
 *
 * 🔑 A BLOCKED FIGURE STAYS BLOCKED. DoctorNow `ourCost` renders its Q1 banner with BOTH
 * readings side by side and no winner; missing lines read `— / Missing`. Never $0.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  formatMetricValue,
  isBlockedMetric,
  describeMetricBasis,
  describeMedicationBasis,
  buildCadenceFills,
  buildTitrationLadder,
  listOfferedTerms
} from '../../utils/revenueMetrics';
import TitrationLadder, { LadderCaption } from './RevenueTitrationLadder';
import { revenueCalculationApi } from '../../services/api';
import styles from '../../pages/admin/RevenueCalculationPage.module.css';

/** The caveat §Cadence model requires beside consultation cadence. Stated, not assumed. */
export const CONSULT_CADENCE_TEXT =
  'Per shipment — unless the provider contract says otherwise. No contract term is recorded '
  + 'anywhere in the schema, so this cadence is the code’s behaviour, not a ratified rule.';

/** The Q1 text. It must appear wherever `ourCost` would otherwise be silently resolved. */
export const Q1_TEXT =
  'Q1 — DoctorNow supply_pricing_meta.ourCost: its per-month versus whole-term meaning is not '
  + 'ratified. Both readings are shown; this page does not pick one, so medication cost, total '
  + 'variable expense and profit stay blocked for this row.';

/** The expense lines whose multiplier is FILLS, not months — the §Cadence model rule. */
const PER_SHIPMENT_LINES = Object.freeze([
  { key: 'consultationCost', label: 'Consultation' },
  { key: 'ancillaryCost', label: 'Ancillary / syringe / cold-chain' },
  { key: 'shippingCost', label: 'Shipping' }
]);

/** Pure. One figure with its basis tooltip; blocked values keep the blocked styling. */
const Figure = ({ metric, testId }) => (
  <span
    className={isBlockedMetric(metric) ? styles.blockedValue : styles.metricValue}
    data-testid={testId}
    data-blocked={String(isBlockedMetric(metric))}
    title={describeMetricBasis(metric)}
  >
    {formatMetricValue(metric)}
  </span>
);

/** Pure. The fills of the selected term, each labelled with the months it covers. */
const CadenceFills = ({ cadence }) => {
  const fills = buildCadenceFills(cadence);
  return (
    <div className={styles.fillStrip} data-testid="cadence-fills" data-shipments={fills.length}>
      {fills.map((fill) => (
        <div key={fill.ordinal} className={styles.fill} data-fill={fill.ordinal}>
          <span className={styles.fillOrdinal}>Fill {fill.ordinal}</span>
          <span className={styles.fillMonths}>{fill.months} months</span>
          <span className={styles.fillRange}>month {fill.startMonth}–{fill.endMonth}</span>
        </div>
      ))}
    </div>
  );
};

/** Pure. Everything that depends on the selected dosage AND the selected term. */
const SelectedTermPanel = ({ row, consultation }) => {
  const shipments = row?.cadence?.shipments ?? 0;
  const medication = row?.medicationCost;
  return (
    <>
      <div className={styles.cadenceHead}>
        <span data-testid="max-supply">
          Max supply <strong>{row?.cadence?.maxSupplyMonths ?? '—'} months</strong>
        </span>
        <span data-testid="shipment-count">
          <strong>{shipments}</strong> {shipments === 1 ? 'fill / shipment' : 'fills / shipments'}
        </span>
        <span data-testid="internal-months">
          <strong>{row?.term?.months ?? '—'}</strong> internal months
        </span>
      </div>

      <CadenceFills cadence={row?.cadence} />

      {medication?.dataQuality === 'conflicting' && (
        <p className={styles.q1Banner} data-testid="q1-banner">
          {Q1_TEXT}
          <span className={styles.q1Readings}>
            <span>whole-term (code): <Figure metric={{ value: medication?.readings?.ourCostWholeTerm }} /></span>
            <span>per-month × months (data): <Figure metric={{ value: medication?.readings?.ourCostPerMonthScaled }} /></span>
          </span>
        </p>
      )}

      <dl className={styles.lineList} data-testid="timeline-lines">
        <div className={styles.line} data-line="medicationCost">
          <dt>Medication</dt>
          <dd>
            <Figure metric={medication} testId="medication-figure" />
            <span className={styles.lineBasis} data-testid="medication-basis">
              basis: {describeMedicationBasis(medication)}
            </span>
          </dd>
        </div>
        {PER_SHIPMENT_LINES.map((line) => (
          <div className={styles.line} key={line.key} data-line={line.key}>
            <dt>{line.label}</dt>
            <dd>
              <Figure metric={row?.[line.key]} />
              <span className={styles.multiplier} data-testid={`multiplier-${line.key}`}>
                × {shipments} {shipments === 1 ? 'shipment' : 'shipments'} — not × {row?.term?.months} months
              </span>
            </dd>
          </div>
        ))}
        <div className={styles.line} data-line="consultationType">
          <dt>Consultation type</dt>
          <dd>
            <span className={styles.metricValue} data-testid="consult-type">
              {consultation?.type?.value ?? '— / Missing'}
            </span>
            <span className={styles.lineBasis}>{describeMetricBasis(consultation?.type)}</span>
            <span className={styles.lineBasis} data-testid="consult-cadence">{CONSULT_CADENCE_TEXT}</span>
            <span className={styles.lineBasis} data-testid="consult-split">
              Sync vs async price split: <Figure metric={consultation?.syncAsyncSplit} /> (Q2)
            </span>
          </dd>
        </div>
        <div className={styles.line} data-line="fees">
          <dt>Ladder fees</dt>
          <dd>
            <span data-testid="fee-strength">
              Increased strength: <Figure metric={row?.fees?.increasedStrength} />
            </span>
            <span data-testid="fee-refill">Refill: <Figure metric={row?.fees?.refill} /></span>
            <span className={styles.lineBasis}>{describeMetricBasis(row?.fees?.increasedStrength)}</span>
          </dd>
        </div>
      </dl>
    </>
  );
};

const RevenueTimeline = ({ productId }) => {
  const [timeline, setTimeline] = useState(null);
  const [error, setError] = useState(null);
  const [selectedDosageId, setSelectedDosageId] = useState(null);
  const [selectedTermKey, setSelectedTermKey] = useState(null);

  useEffect(() => {
    let live = true;
    revenueCalculationApi
      .getProductTimeline(productId)
      .then((data) => { if (live) { setTimeline(data); setError(null); } })
      .catch((err) => { if (live) setError(err?.message || 'Failed to load timeline'); });
    return () => { live = false; };
  }, [productId]);

  const terms = useMemo(() => listOfferedTerms(timeline?.titration), [timeline]);
  const termKey = selectedTermKey ?? terms[0]?.key ?? null;
  const ladder = useMemo(
    () => buildTitrationLadder(timeline?.titration, termKey),
    [timeline, termKey]
  );

  // Opens on the CATALOG DEFAULT step, not on ladder[0] — those differ product by product.
  const defaultDosageId = ladder.find((step) => step.row?.dosage?.isDefaultStep)?.dosageId
    ?? ladder[0]?.dosageId
    ?? null;
  const dosageId = selectedDosageId ?? defaultDosageId;
  const selected = ladder.find((step) => step.dosageId === dosageId) ?? ladder[0] ?? null;

  if (error) return <p className={styles.error} data-testid="timeline-error">{error}</p>;
  if (!timeline) return <p className={styles.timelineLoading}>Loading cadence timeline…</p>;
  if (terms.length === 0) {
    return (
      <p className={styles.notSold} data-testid="timeline-empty">
        This product sells no supply term, so it has no cadence to show.
      </p>
    );
  }

  return (
    <section
      className={styles.timeline}
      data-testid={`revenue-timeline-${productId}`}
      data-selected-term={termKey}
      data-selected-dosage={dosageId ?? ''}
    >
      <header className={styles.timelineHead}>
        <h4 className={styles.timelineTitle}>Term, titration and cadence</h4>
        <div className={styles.termPicker} role="group" aria-label="Supply term">
          {terms.map((term) => (
            <button
              key={term.key}
              type="button"
              className={styles.termOption}
              data-testid="term-option"
              data-term-key={term.key}
              data-selected={String(term.key === termKey)}
              aria-pressed={term.key === termKey}
              onClick={() => setSelectedTermKey(term.key)}
            >
              {term.label}
            </button>
          ))}
        </div>
      </header>

      <TitrationLadder ladder={ladder} selectedDosageId={dosageId} onSelect={setSelectedDosageId} />
      <LadderCaption ladder={ladder} />

      {selected?.row
        ? <SelectedTermPanel row={selected.row} consultation={selected.consultation} />
        : (
          <p className={styles.notSold} data-testid="term-not-sold">
            {selected?.dosageAmount ?? 'This dosage'} is not sold on the selected term.
          </p>
        )}
    </section>
  );
};

export default RevenueTimeline;
